import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import {
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
  Card,
  CardBody,
  Spinner,
  Modal,
  ModalBody,
} from "reactstrap";
import DataTable from "react-data-table-component";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from "firebase/firestore";
import { getVersionedCollection } from "../../utils/firestorePaths";
import useDebounce from "../../components/useDebounce";
import PlateFormat from "../../components/PlateFormat";
import AIChatButton from "../../components/Common/AIChatButton";
import { formatCurrency } from "../../utils/formatCurrency";

const RESULTS_PER_PAGE = 30;

// --- Helpers ---
function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return (
    date.getDate().toString().padStart(2, "0") + "/" +
    (date.getMonth() + 1).toString().padStart(2, "0") + "/" +
    date.getFullYear() + " " +
    date.getHours().toString().padStart(2, "0") + ":" +
    date.getMinutes().toString().padStart(2, "0")
  );
}

function formatDateShort(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return (
    date.getDate().toString().padStart(2, "0") + "/" +
    (date.getMonth() + 1).toString().padStart(2, "0") + " " +
    date.getHours().toString().padStart(2, "0") + ":" +
    date.getMinutes().toString().padStart(2, "0")
  );
}

function formatPrice(price) {
  return formatCurrency(price, true);
}

function formatTimeDifference(init_date_str, end_date_str) {
  const diff = new Date(end_date_str) - new Date(init_date_str);
  const days = Math.floor(diff / (24 * 3600 * 1000));
  const hours = Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000));
  const minutes = Math.floor((diff % (3600 * 1000)) / (60 * 1000));
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}min`);
  return parts.join(" ");
}

const TYPE_CONFIG = {
  hour: { bg: "#3b82f6", label: "xHora" },
  stay: { bg: "#22c55e", label: "xEstadía" },
  tn: { bg: "#ef4444", label: "xTurno" },
  // El desktop graba type='plana' desde v2.0.5; sin esta entrada el ticket
  // se mostraba como "--". Color alineado con el Tarifario.
  plana: { bg: "#a569bd", label: "xPlana" },
};
const getTypeConfig = (type) => TYPE_CONFIG[type] || { bg: "#6b7280", label: "--" };

// --- Mobile detection ---
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
};

// --- Mobile Card ---
const MobileTicketCard = ({ ticket, country, onClick }) => {
  const cfg = getTypeConfig(ticket.type);
  const duration = ticket.init_date && ticket.end_date
    ? formatTimeDifference(ticket.init_date, ticket.end_date)
    : "";

  return (
    <Card className="mb-2 shadow-sm" style={{ borderLeft: `4px solid ${cfg.bg}`, borderRadius: 8, cursor: "pointer" }} onClick={onClick}>
      <CardBody className="py-2 px-3">
        <div className="d-flex justify-content-between align-items-center">
          <span style={{ fontSize: 16, fontWeight: 700 }}>{PlateFormat(ticket.plate, country)}</span>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{formatPrice(ticket.value)}</span>
        </div>
        <div className="d-flex align-items-center gap-1 mt-1">
          <Badge color="dark" style={{ fontSize: 12 }}>{ticket.category_name}</Badge>
          <Badge style={{ fontSize: 12, backgroundColor: cfg.bg, color: "#fff" }}>{cfg.label}</Badge>
          {duration && <span className="text-muted" style={{ fontSize: 11 }}>{duration}</span>}
        </div>
        {ticket.description && (
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>{ticket.description}</div>
        )}
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span className="text-muted" style={{ fontSize: 12 }}>
            <span className="material-symbols-rounded me-1 text-success" style={{ fontSize: 14 }}>arrow_back</span>
            {formatDateShort(ticket.end_date)}
          </span>
          <span className="text-muted" style={{ fontSize: 11 }}>{ticket.closed_by_fullName || ticket.fullName}</span>
        </div>
      </CardBody>
    </Card>
  );
};

// --- Detail Modal ---
const ClosedTicketDetailModal = ({ ticket, isOpen, toggle, country, hasAnpr }) => {
  if (!ticket) return null;
  const cfg = getTypeConfig(ticket.type);
  const plate = PlateFormat(ticket.plate, country);
  const duration = ticket.init_date && ticket.end_date
    ? formatTimeDifference(ticket.init_date, ticket.end_date)
    : "";
  const imgSrc = ticket.picture ? `data:image/png;base64,${ticket.picture}` : null;
  const editLog = (() => { try { return Array.isArray(ticket.edit_log) ? ticket.edit_log : JSON.parse(ticket.edit_log || '[]') } catch { return [] } })();

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalBody className="p-0">
        {hasAnpr && imgSrc && (
          <img
            src={imgSrc}
            alt={ticket.plate}
            style={{ width: "100%", maxHeight: "50vh", objectFit: "cover" }}
          />
        )}
        <div className="p-3">
        {/* Plate + value */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>{plate}</span>
          <span style={{ fontSize: 18, fontWeight: 700 }}>{formatPrice(ticket.value)}</span>
        </div>

        {/* Badges */}
        <div className="d-flex gap-1 mb-2">
          <Badge color="dark" style={{ fontSize: 12 }}>{ticket.category_name}</Badge>
          <Badge style={{ fontSize: 12, backgroundColor: cfg.bg, color: "#fff" }}>{cfg.label}</Badge>
        </div>

        {/* Description */}
        {ticket.description && (
          <div className="text-muted mb-2" style={{ fontSize: 13 }}>{ticket.description}</div>
        )}

        {/* Canceled details (for canceled tickets) */}
        {ticket.canceled_details && (
          <div className="mb-2" style={{ fontSize: 13, color: "#92400e" }}>
            <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>error</span>{ticket.canceled_details}
          </div>
        )}

        <hr className="my-2" />

        {/* Times */}
        <div style={{ fontSize: 13 }}>
          <div>
            <span className="material-symbols-rounded me-1 text-success" style={{ fontSize: 16 }}>arrow_forward</span>
            Ingreso: {formatDate(ticket.init_date)}
          </div>
          <div>
            <span className="material-symbols-rounded me-1 text-danger" style={{ fontSize: 16 }}>arrow_back</span>
            Cierre: {formatDate(ticket.end_date)}
            {duration && <span className="ms-2 text-muted">({duration})</span>}
          </div>
        </div>

        <hr className="my-2" />

        {/* Operators */}
        <div className="d-flex justify-content-between" style={{ fontSize: 12 }}>
          <div className="text-muted">
            <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>person</span>Abierto: {ticket.fullName}
          </div>
          {ticket.closed_by_fullName && (
            <div className="text-muted">
              Cerrado: {ticket.closed_by_fullName}
            </div>
          )}
        </div>

        {ticket.id && (
          <div className="text-end mt-1">
            <span className="text-muted" style={{ fontSize: 11 }}>#{ticket.id}</span>
          </div>
        )}

        {/* Edit Log */}
        {editLog.length > 0 && (<>
          <hr className="my-2" />
          <div style={{ fontSize: 12 }}>
            <div className="d-flex align-items-center gap-1 mb-2">
              <span className="material-symbols-rounded" style={{ fontSize: 16, color: "#888" }}>edit_note</span>
              <span style={{ fontWeight: 600 }}>Historial de ediciones</span>
            </div>
            {editLog.map((entry, i) => {
              const labels = { plate: "Patente", type: "Tipo", category: "Categoría", category_id: "Categoría", car_detail: "Vehículo", deal: "Acuerdo" };
              const dateStr = (entry.date || "").replace(/(\d{4})-(\d{2})-(\d{2})/, "$3/$2/$1").substring(0, 16);
              return (
                <div key={i} style={{ marginBottom: i < editLog.length - 1 ? 8 : 0 }}>
                  {i > 0 && <hr className="my-1" style={{ opacity: 0.15 }} />}
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {dateStr} — <strong>{entry.user || "?"}</strong>
                  </div>
                  {Object.entries(entry.changes || {}).map(([key, vals]) => (
                    <div key={key} className="text-muted" style={{ fontSize: 12 }}>
                      {labels[key] || key}: {vals[0] || "-"} → {vals[1] || "-"}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </>)}
        </div>
      </ModalBody>
    </Modal>
  );
};

// --- Main Component ---
const ClosedTickets = ({ currentCompany, appConfig, syncVersion }) => {
  const isMobile = useIsMobile();
  const country = currentCompany?.country;
  const hasAnpr = appConfig?.modules?.anpr === 1;

  const [tickets, setTickets] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [searchDate, setSearchDate] = useState("");
  const [searchPlate, setSearchPlate] = useState("");
  const debouncedSearchPlate = useDebounce(searchPlate, 500);

  const fetchTickets = useCallback(
    async (reset = false) => {
      if (!currentCompany?.id) return;
      if (loading || (!hasMore && !reset)) return;

      setLoading(true);
      const ticketsRef = getVersionedCollection(currentCompany.id, "tickets", syncVersion);

      const constraints = [
        where("closed", "==", 1),
        where("canceled", "==", 0),
      ];

      const hasPlateSearch = debouncedSearchPlate && debouncedSearchPlate.length > 0;

      if (hasPlateSearch) {
        const plateUpper = debouncedSearchPlate.trim().toUpperCase();
        constraints.push(where("plate", ">=", plateUpper));
        constraints.push(where("plate", "<=", plateUpper + "\uf8ff"));
        constraints.push(orderBy("plate"));
      } else if (searchDate) {
        constraints.push(orderBy("end_date", "desc"));
        constraints.push(where("end_date", ">=", `${searchDate} 00:00:00.0000`));
        constraints.push(where("end_date", "<=", `${searchDate} 23:59:59.0000`));
      } else {
        constraints.push(orderBy("end_date", "desc"));
      }

      if (!reset && lastVisible) {
        constraints.push(startAfter(lastVisible));
      }
      constraints.push(limit(RESULTS_PER_PAGE));

      try {
        const q = query(ticketsRef, ...constraints);
        const snapshot = await getDocs(q);
        let newTickets = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // Si buscamos por placa + fecha, filtramos fecha en el cliente
        if (hasPlateSearch && searchDate) {
          const startOfDay = `${searchDate} 00:00:00.0000`;
          const endOfDay = `${searchDate} 23:59:59.0000`;
          newTickets = newTickets.filter(
            (t) => t.end_date >= startOfDay && t.end_date <= endOfDay
          );
        }

        if (snapshot.docs.length > 0) {
          setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
          setTickets((prev) => (reset ? newTickets : [...prev, ...newTickets]));
          if (snapshot.docs.length < RESULTS_PER_PAGE) setHasMore(false);
        } else {
          if (reset) setTickets([]);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching closed tickets:", error);
        setHasMore(false);
      }
      setLoading(false);
    },
    [currentCompany?.id, syncVersion, searchDate, debouncedSearchPlate, lastVisible, loading, hasMore]
  );

  useEffect(() => {
    if (!currentCompany?.id) return;
    setTickets([]);
    setLastVisible(null);
    setHasMore(true);
    fetchTickets(true);
  }, [searchDate, debouncedSearchPlate, currentCompany?.id, syncVersion]);

  // --- Desktop columns ---
  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "70px",
    },
    {
      name: "Cierre",
      selector: (row) => row.end_date,
      sortable: true,
      cell: (row) => formatDate(row.end_date),
      width: "150px",
    },
    {
      name: "Matrícula",
      selector: (row) => row.plate,
      sortable: true,
      cell: (row) => <span style={{ fontWeight: 700 }}>{PlateFormat(row.plate, country)}</span>,
      width: "130px",
    },
    {
      name: "Categoría",
      selector: (row) => row.category_name,
      sortable: true,
      width: "120px",
    },
    {
      name: "Tipo",
      selector: (row) => row.type,
      sortable: true,
      cell: (row) => {
        const cfg = getTypeConfig(row.type);
        return <Badge style={{ fontSize: 11, backgroundColor: cfg.bg, color: "#fff" }}>{cfg.label}</Badge>;
      },
      width: "90px",
    },
    {
      name: "Descripción",
      selector: (row) => row.description,
      sortable: true,
      wrap: true,
      grow: 3,
    },
    {
      name: "Duración",
      selector: (row) => row.duration,
      sortable: false,
      cell: (row) =>
        row.init_date && row.end_date ? formatTimeDifference(row.init_date, row.end_date) : "",
      width: "100px",
    },
    {
      name: "Valor",
      selector: (row) => row.value,
      sortable: true,
      cell: (row) => <span style={{ fontWeight: 600 }}>{formatPrice(row.value)}</span>,
      width: "100px",
    },
    {
      name: "Abierto por",
      selector: (row) => row.fullName,
      sortable: true,
      wrap: true,
      width: "140px",
    },
    {
      name: "Cerrado por",
      selector: (row) => row.closed_by_fullName,
      sortable: true,
      wrap: true,
      width: "140px",
    },
  ];

  const customTableStyles = {
    headRow: {
      style: {
        position: "sticky",
        top: 0,
        backgroundColor: "#fff",
        zIndex: 2,
        boxShadow: "0px 2px 2px rgba(0,0,0,0.1)",
      },
    },
  };

  return (
    <>
      {/* Filters */}
      <Row className="mb-3 g-2 align-items-center">
        <Col xs={6} md="auto">
          <InputGroup size={isMobile ? "sm" : ""}>
            <InputGroupText style={{ backgroundColor: "#f8f9fa", border: "1px solid #ced4da", borderRight: 0 }}>
              <span className="material-symbols-rounded text-muted" style={{ fontSize: 16 }}>calendar_today</span>
            </InputGroupText>
            <Input
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              style={{ fontSize: 14, borderLeft: 0 }}
            />
          </InputGroup>
        </Col>
        <Col xs={6} md="auto">
          <InputGroup size={isMobile ? "sm" : ""}>
            <InputGroupText style={{ backgroundColor: "#f8f9fa", border: "1px solid #ced4da", borderRight: 0 }}>
              <span className="material-symbols-rounded text-muted" style={{ fontSize: 16 }}>search</span>
            </InputGroupText>
            <Input
              type="search"
              placeholder="Buscar matrícula..."
              value={searchPlate}
              onChange={(e) => setSearchPlate(e.target.value)}
              style={{ fontSize: 14, borderLeft: 0 }}
            />
          </InputGroup>
        </Col>
        {(searchDate || searchPlate) && (
          <Col xs="auto">
            <button
              className="btn btn-outline-danger btn-sm"
              style={{ borderRadius: 20, fontSize: 12 }}
              onClick={() => { setSearchDate(""); setSearchPlate(""); }}
            >
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>close</span>Limpiar
            </button>
          </Col>
        )}
        {loading && tickets.length === 0 && (
          <Col xs="auto" className="d-flex align-items-center">
            <Spinner size="sm" color="primary" />
          </Col>
        )}
      </Row>

      {/* Content */}
      <div
        id="closedScrollable"
        style={{ height: "calc(100vh - 280px)", overflowY: "auto", overflowX: "auto" }}
      >
        <InfiniteScroll
          dataLength={tickets.length}
          next={fetchTickets}
          hasMore={hasMore}
          loader={tickets.length > 0 ? <div className="text-center py-3 text-muted">Cargando más...</div> : null}
          scrollableTarget="closedScrollable"
        >
          {isMobile ? (
            tickets.length === 0 && !loading ? (
              <p className="text-muted text-center py-4">No hay resultados</p>
            ) : (
              tickets.map((t) => <MobileTicketCard key={t.id} ticket={t} country={country} onClick={() => setSelectedTicket(t)} />)
            )
          ) : (
            <div style={{ minWidth: "1200px" }}>
              <DataTable
                columns={columns}
                data={tickets}
                pagination={false}
                highlightOnHover
                pointerOnHover
                onRowClicked={(row) => setSelectedTicket(row)}
                noDataComponent={
                  <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {loading ? <Spinner color="primary" /> : "No hay resultados"}
                  </div>
                }
                customStyles={customTableStyles}
              />
            </div>
          )}
        </InfiniteScroll>
      </div>

      <ClosedTicketDetailModal
        ticket={selectedTicket}
        isOpen={!!selectedTicket}
        toggle={() => setSelectedTicket(null)}
        country={country}
        hasAnpr={hasAnpr}
      />

      {/* AI Chat */}
      <ClosedTicketsAI tickets={tickets} hasMore={hasMore} />
    </>
  );
};

// ---- AI Data Summary for closed tickets ----
const ClosedTicketsAI = ({ tickets, hasMore }) => {
  const aiDataSummary = useMemo(() => {
    if (!tickets || !tickets.length) return "";

    let out = `TICKETS CERRADOS (${tickets.length} registros cargados)\n\n`;

    // Revenue
    const totalRevenue = tickets.reduce((a, t) => a + (t.value || 0), 0);
    const avgTicket = totalRevenue / tickets.length;
    out += `INGRESOS:\n`;
    out += `- Total recaudado: ${formatPrice(totalRevenue)}\n`;
    out += `- Promedio por ticket: ${formatPrice(avgTicket)}\n\n`;

    // By type
    const byType = {};
    tickets.forEach(t => {
      const label = TYPE_CONFIG[t.type]?.label || "Otro";
      if (!byType[label]) byType[label] = { count: 0, total: 0 };
      byType[label].count++;
      byType[label].total += t.value || 0;
    });
    out += `POR TIPO:\n`;
    Object.entries(byType).forEach(([k, v]) => {
      const pct = ((v.count / tickets.length) * 100).toFixed(0);
      out += `- ${k}: ${v.count} tickets (${pct}%), total ${formatPrice(v.total)}\n`;
    });
    out += "\n";

    // By category
    const byCategory = {};
    tickets.forEach(t => {
      const cat = t.category_name || "Sin categoria";
      if (!byCategory[cat]) byCategory[cat] = { count: 0, total: 0 };
      byCategory[cat].count++;
      byCategory[cat].total += t.value || 0;
    });
    if (Object.keys(byCategory).length > 1) {
      out += `POR CATEGORIA:\n`;
      Object.entries(byCategory).sort((a, b) => b[1].count - a[1].count).forEach(([k, v]) => {
        out += `- ${k}: ${v.count} tickets, ${formatPrice(v.total)}\n`;
      });
      out += "\n";
    }

    // Duration analysis
    const durations = tickets
      .filter(t => t.init_date && t.end_date)
      .map(t => (new Date(t.end_date) - new Date(t.init_date)) / 60000);
    if (durations.length) {
      const avgDur = durations.reduce((a, b) => a + b, 0) / durations.length;
      const maxDur = Math.max(...durations);
      const fmtDur = (mins) => {
        const h = Math.floor(mins / 60);
        const m = Math.round(mins % 60);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
      };
      out += `PERMANENCIA:\n`;
      out += `- Promedio: ${fmtDur(avgDur)}\n`;
      out += `- Maxima: ${fmtDur(maxDur)}\n\n`;
    }

    // By operator (closed by)
    const byOperator = {};
    tickets.forEach(t => {
      const op = t.closed_by_fullName || t.fullName || "Sin operador";
      if (!byOperator[op]) byOperator[op] = { count: 0, total: 0 };
      byOperator[op].count++;
      byOperator[op].total += t.value || 0;
    });
    if (Object.keys(byOperator).length > 1) {
      out += `POR OPERADOR (COBRO):\n`;
      Object.entries(byOperator).sort((a, b) => b[1].total - a[1].total).forEach(([k, v]) => {
        out += `- ${k}: ${v.count} tickets, ${formatPrice(v.total)}\n`;
      });
      out += "\n";
    }

    // Top 5 highest value
    const sorted = [...tickets].sort((a, b) => (b.value || 0) - (a.value || 0));
    const top5 = sorted.slice(0, 5);
    if (top5.length) {
      out += `TICKETS DE MAYOR VALOR:\n`;
      top5.forEach(t => {
        const dur = t.init_date && t.end_date ? formatTimeDifference(t.init_date, t.end_date) : "-";
        out += `- ${formatPrice(t.value)} | ${t.plate || "-"} | ${t.category_name || "-"} | ${dur} | ${formatDate(t.end_date)}\n`;
      });
    }

    return out;
  }, [tickets]);

  const dataScope = hasMore
    ? `Analizando los ${tickets.length} tickets cargados. Desplaza hacia abajo para cargar mas datos.`
    : null;

  const aiSuggestions = useMemo(() => [
    "¿Cuanto se recaudo en total?",
    "¿Cual es el ticket promedio?",
    "¿Que tipo de ticket se usa mas?",
    "¿Cual fue la permanencia promedio?",
    "¿Que operador cobro mas?",
    "¿Cuales fueron los tickets mas caros?",
  ], []);

  if (!aiDataSummary) return null;
  return (
    <AIChatButton
      context="tickets_closed"
      dataSummary={aiDataSummary}
      suggestions={aiSuggestions}
      title="Asistente Tickets Cerrados"
      dataScope={dataScope}
    />
  );
};

const mapStateToProps = (state) => ({
  currentCompany: state.companies.currentCompany,
  appConfig: state.data.appConfig,
  syncVersion: state.data.syncVersion,
});

export default connect(mapStateToProps)(ClosedTickets);
